import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

const DashboardNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const submenuRef = useRef(null);

  const userRole = userProfile?.role || 'member';

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (submenuRef?.current && !submenuRef?.current?.contains(event?.target)) {
        setOpenSubmenu(null);
      }
    };

    if (openSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openSubmenu]);

  const roleSubNavigationMap = {
    member: [
      { label: 'Overview', path: '/member-dashboard', icon: 'Home', description: 'Your fitness summary' },
      { label: 'Check-In', path: '/mobile-check-in-hub', icon: 'QrCode', description: 'Mobile check-in' },
      { label: 'Book Classes', path: '/class-booking', icon: 'Calendar', description: 'Schedule your workouts' },
      { label: 'Programs', path: '/workout-programs', icon: 'Dumbbell', description: 'Training plans' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare', description: 'Chat with trainers' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor', description: 'Manage login sessions' },
    ],
    staff: [
      { label: 'Overview', path: '/staff-dashboard', icon: 'Home', description: 'Operations summary' },
      { label: 'Attendance', path: '/staff-attendance-tracking', icon: 'Clock', description: 'Time tracking' },
      { label: 'Members', path: '/member-management', icon: 'Users', description: 'Manage memberships' },
      { label: 'POS', path: '/location-based-pos-inventory', icon: 'ShoppingCart', description: 'Point of sale' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio', description: 'Team communications' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare', description: 'Member communications' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor', description: 'Manage login sessions' },
    ],
    trainer: [
      { label: 'Overview', path: '/trainer-dashboard', icon: 'Home', description: 'Client overview' },
      { label: 'Attendance', path: '/staff-attendance-tracking', icon: 'Clock', description: 'Time tracking' },
      { label: 'Schedule', path: '/class-booking', icon: 'Calendar', description: 'Class schedule' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare', description: 'Client messages' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor', description: 'Manage login sessions' },
    ],
    manager: [
      { label: 'Dashboard', path: '/staff-dashboard', icon: 'Home', description: 'Operations overview' },
      { 
        label: 'Analytics', 
        icon: 'BarChart3', 
        description: 'Performance metrics',
        submenu: [
          { label: 'Business Analytics', path: '/analytics-dashboard', icon: 'BarChart3', description: 'Revenue & member insights' },
          { label: 'Staff Performance', path: '/staff-performance-analytics', icon: 'TrendingUp', description: 'Staff productivity metrics' },
        ]
      },
      { label: 'Operations', path: '/live-operations-feed', icon: 'Activity', description: 'Live activity feed' },
      { label: 'Attendance', path: '/staff-attendance-tracking', icon: 'Clock', description: 'Staff attendance' },
      { label: 'Members', path: '/member-management', icon: 'Users', description: 'Member database' },
      { label: 'Retention', path: '/member-retention', icon: 'Heart', description: 'Member retention' },
      { label: 'Equipment', path: '/equipment-management', icon: 'Package', description: 'Equipment tracking' },
      { label: 'Lifecycle', path: '/equipment-lifecycle-management', icon: 'Activity', description: 'Equipment lifecycle' },
      { label: 'Maintenance', path: '/automated-maintenance-scheduling', icon: 'Calendar', description: 'Maintenance scheduling' },
      { label: 'Work Orders', path: '/work-order-management-system', icon: 'ClipboardList', description: 'Work order system' },
      { label: 'Mobile Ops', path: '/mobile-maintenance-operations', icon: 'Smartphone', description: 'Mobile maintenance operations' },
      { label: 'Location POS', path: '/location-based-pos-inventory', icon: 'MapPin', description: 'Multi-location POS' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio', description: 'Staff coordination' },
      { label: 'Financials', path: '/financial-reconciliation-dashboard', icon: 'DollarSign', description: 'Financial reconciliation' },
      { label: 'Reports', path: '/automated-report-generation', icon: 'FileText', description: 'Automated reports' },
      { label: 'Alerts', path: '/critical-alert-management-center', icon: 'Bell', description: 'Critical alert management' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare', description: 'Team communications' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor', description: 'Manage login sessions' },
    ],
    owner: [
      { label: 'Single Gym', path: '/single-gym-owner-dashboard', icon: 'Building', description: 'My gym overview' },
      { 
        label: 'Analytics', 
        icon: 'BarChart3', 
        description: 'Business insights',
        submenu: [
          { label: 'Business Analytics', path: '/analytics-dashboard', icon: 'BarChart3', description: 'Revenue & member insights' },
          { label: 'Staff Performance', path: '/staff-performance-analytics', icon: 'TrendingUp', description: 'Staff productivity metrics' },
        ]
      },
      { label: 'Operations', path: '/live-operations-feed', icon: 'Activity', description: 'Live activity feed' },
      { label: 'Attendance', path: '/staff-attendance-tracking', icon: 'Clock', description: 'Staff attendance' },
      { label: 'Members', path: '/member-management', icon: 'Users', description: 'Member overview' },
      { label: 'Retention', path: '/member-retention', icon: 'Heart', description: 'Member retention' },
      { label: 'Equipment', path: '/equipment-management', icon: 'Package', description: 'Equipment tracking' },
      { label: 'Lifecycle', path: '/equipment-lifecycle-management', icon: 'Activity', description: 'Equipment lifecycle' },
      { label: 'Maintenance', path: '/automated-maintenance-scheduling', icon: 'Calendar', description: 'Maintenance scheduling' },
      { label: 'Work Orders', path: '/work-order-management-system', icon: 'ClipboardList', description: 'Work order system' },
      { label: 'Mobile Ops', path: '/mobile-maintenance-operations', icon: 'Smartphone', description: 'Mobile maintenance operations' },
      { label: 'Location POS', path: '/location-based-pos-inventory', icon: 'MapPin', description: 'Multi-location POS' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio', description: 'Staff coordination' },
      { label: 'Financials', path: '/financial-reconciliation-dashboard', icon: 'DollarSign', description: 'Financial reconciliation' },
      { label: 'Reports', path: '/automated-report-generation', icon: 'FileText', description: 'Automated reports' },
      { label: 'Alerts', path: '/critical-alert-management-center', icon: 'Bell', description: 'Critical alert management' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare', description: 'Organization-wide chat' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor', description: 'Manage login sessions' },
    ],
    sysadmin: [
      { label: 'System Overview', path: '/staff-dashboard', icon: 'Shield', description: 'System administration' },
      { label: 'Sysadmin Dashboard', path: '/system-administrator-dashboard', icon: 'Server', description: 'Platform oversight' },
      { label: 'Single Gym', path: '/single-gym-owner-dashboard', icon: 'Building', description: 'Gym management' },
      { 
        label: 'Analytics', 
        icon: 'BarChart3', 
        description: 'System analytics',
        submenu: [
          { label: 'Business Analytics', path: '/analytics-dashboard', icon: 'BarChart3', description: 'Revenue & member insights' },
          { label: 'Staff Performance', path: '/staff-performance-analytics', icon: 'TrendingUp', description: 'Staff productivity metrics' },
        ]
      },
      { label: 'Operations', path: '/live-operations-feed', icon: 'Activity', description: 'Live activity feed' },
      { label: 'Attendance', path: '/staff-attendance-tracking', icon: 'Clock', description: 'Staff attendance' },
      { label: 'Members', path: '/member-management', icon: 'Users', description: 'User management' },
      { label: 'Retention', path: '/member-retention', icon: 'Heart', description: 'Member retention' },
      { label: 'Equipment', path: '/equipment-management', icon: 'Package', description: 'Equipment tracking' },
      { label: 'Lifecycle', path: '/equipment-lifecycle-management', icon: 'Activity', description: 'Equipment lifecycle' },
      { label: 'Maintenance', path: '/automated-maintenance-scheduling', icon: 'Calendar', description: 'Maintenance scheduling' },
      { label: 'Work Orders', path: '/work-order-management-system', icon: 'ClipboardList', description: 'Work order system' },
      { label: 'Mobile Ops', path: '/mobile-maintenance-operations', icon: 'Smartphone', description: 'Mobile maintenance operations' },
      { label: 'Location POS', path: '/location-based-pos-inventory', icon: 'MapPin', description: 'Multi-location POS' },
      { label: 'Staff Hub', path: '/staff-messaging-hub', icon: 'Radio', description: 'Staff coordination' },
      { label: 'Financials', path: '/financial-reconciliation-dashboard', icon: 'DollarSign', description: 'Financial reconciliation' },
      { label: 'Reports', path: '/automated-report-generation', icon: 'FileText', description: 'Automated reports' },
      { label: 'Messages', path: '/messaging', icon: 'MessageSquare', description: 'System communications' },
      { label: 'Sessions', path: '/session-management-center', icon: 'Monitor', description: 'Manage login sessions' },
    ],
  };

  const subNavigationItems = roleSubNavigationMap?.[userRole] || roleSubNavigationMap?.member;

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const isSubmenuActive = (submenu) => {
    return submenu?.some(item => location?.pathname === item?.path);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpenSubmenu(null);
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  return (
    <div className="bg-card border-b border-border">
      <div className="container mx-auto px-6 lg:px-8">
        <nav className="flex items-center gap-1 overflow-x-auto custom-scrollbar py-2">
          {subNavigationItems?.map((item) => (
            <div key={item?.label} className="relative" ref={item?.submenu ? submenuRef : null}>
              {item?.submenu ? (
                // Menu item with submenu
                <>
                  <button
                    onClick={() => toggleSubmenu(item?.label)}
                    className={`
                      flex items-center gap-3 px-5 py-3 rounded-lg whitespace-nowrap
                      transition-all duration-250 ease-out touch-target
                      ${isSubmenuActive(item?.submenu) || openSubmenu === item?.label
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'text-foreground hover:bg-muted hover:text-primary'
                      }
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
                    `}
                  >
                    <Icon name={item?.icon} size={20} />
                    <div className="text-left">
                      <div className="text-sm font-medium">{item?.label}</div>
                      <div className={`text-xs ${isSubmenuActive(item?.submenu) || openSubmenu === item?.label ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                        {item?.description}
                      </div>
                    </div>
                    <Icon 
                      name={openSubmenu === item?.label ? 'ChevronUp' : 'ChevronDown'} 
                      size={16} 
                      className="ml-1"
                    />
                  </button>
                  
                  {/* Dropdown submenu */}
                  {openSubmenu === item?.label && (
                    <div className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                      {item?.submenu?.map((subItem) => (
                        <button
                          key={subItem?.path}
                          onClick={() => handleNavigation(subItem?.path)}
                          className={`
                            w-full flex items-center gap-3 px-4 py-3 text-left
                            transition-all duration-200
                            ${isActivePath(subItem?.path)
                              ? 'bg-primary text-primary-foreground'
                              : 'text-foreground hover:bg-muted hover:text-primary'
                            }
                            border-b border-border last:border-b-0
                          `}
                        >
                          <Icon name={subItem?.icon} size={18} />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{subItem?.label}</div>
                            <div className={`text-xs ${isActivePath(subItem?.path) ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                              {subItem?.description}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                // Regular menu item without submenu
                <button
                  onClick={() => handleNavigation(item?.path)}
                  className={`
                    flex items-center gap-3 px-5 py-3 rounded-lg whitespace-nowrap
                    transition-all duration-250 ease-out touch-target
                    ${isActivePath(item?.path) 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-foreground hover:bg-muted hover:text-primary'
                    }
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
                  `}
                >
                  <Icon name={item?.icon} size={20} />
                  <div className="text-left">
                    <div className="text-sm font-medium">{item?.label}</div>
                    <div className={`text-xs ${isActivePath(item?.path) ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                      {item?.description}
                    </div>
                  </div>
                </button>
              )}
            </div>
          ))}
          {(userRole === 'member' || userRole === 'trainer') && (
            <>
              <NavLink to="/member-dashboard" icon="LayoutDashboard" label="Dashboard" />
              <NavLink to="/member-communication-hub" icon="MessageSquare" label="Communication" />
              <NavLink to="/class-booking" icon="Calendar" label="Classes" />
            </>
          )}
        </nav>
      </div>
    </div>
  );
};

export default DashboardNavigation;